import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, Leaf, ChevronRight, TrendingUp, Euro, BarChart3, Download,
  Fuel, AlertTriangle, UserX, Receipt, Wrench, RefreshCw, Building2, User,
  Zap, Check, Target, Calendar, Award, ArrowRight, Loader2, Sparkles,
  ChevronDown, Truck, Rocket, FileText
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuickAnalysis = ({ config, clientData, comerciales, onBack, onFinish }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [formData, setFormData] = useState({
    numVehicles: 50, kmPerVehicleYear: 45000, fuelConsumption: 28, fuelPrice: 1.45,
    accidentHoursPercent: 1.5, absenteeismPercent: 0.8, finesPerVehicleYear: 1.2,
    avgFineAmount: 150, maintenanceCostPerVehicle: 3500,
    fuelReductionPercent: 6, accidentReductionPercent: 35, absenteeismReductionPercent: 45,
    fineReductionPercent: 30, maintenanceReductionPercent: 12
  });
  const [resultado, setResultado] = useState(null);
  const [mensajesQuick, setMensajesQuick] = useState(null);
  const [mensajesSeleccionados, setMensajesSeleccionados] = useState({});

  const currentBench = config.benchmarks?.[clientData.sector];
  const comercialActual = comerciales?.find(c => c.id === clientData.comercialId);
  const sectorNames = {
    urbano: 'Transporte Urbano',
    interurbano: 'Transporte Interurbano',
    distribucion: 'Distribución',
    logistica: 'Logística',
    taxi: 'Taxi/VTC'
  };

  // Cargar mensajes Quick al montar
  useEffect(() => {
    const loadMensajes = async () => {
      try {
        const response = await axios.get(`${API}/mensajes-quick`);
        setMensajesQuick(response.data);
        // Seleccionar un mensaje aleatorio de cada categoría
        setMensajesSeleccionados({
          kpis: response.data.kpis[Math.floor(Math.random() * response.data.kpis.length)],
          vsSector: response.data.vsSector[Math.floor(Math.random() * response.data.vsSector.length)],
          mejoras: response.data.mejoras[Math.floor(Math.random() * response.data.mejoras.length)]
        });
      } catch (error) {
        // Mensajes por defecto si falla la carga
        setMensajesSeleccionados({
          kpis: { headline: "El 40% de tus costes de flota son invisibles", subtext: "Multas, bajas, averías... Ponles número" },
          vsSector: { headline: "Tu competencia ya tiene estos datos", subtext: "Descubre si estás perdiendo margen sin saberlo" },
          mejoras: { headline: "El 92% de flotas mejora en 90 días", subtext: "Define tu objetivo y calcula el retorno" }
        });
      }
    };
    loadMensajes();
  }, []);

  useEffect(() => {
    if (config.presets && clientData.sector) {
      const preset = config.presets[clientData.sector];
      if (preset) {
        setFormData(prev => ({ ...prev, ...preset }));
      }
    }
  }, [config.presets, clientData.sector]);

  // Calcular resultado
  const calcularResultado = () => {
    const annualKm = formData.numVehicles * formData.kmPerVehicleYear;
    const annualLiters = (annualKm / 100) * formData.fuelConsumption;
    const fuelSaving = annualLiters * (formData.fuelReductionPercent / 100) * formData.fuelPrice;
    const totalHours = formData.numVehicles * 1800;
    const accidentSaving = totalHours * (formData.accidentHoursPercent / 100) * (formData.accidentReductionPercent / 100) * 35;
    const absentSaving = totalHours * (formData.absenteeismPercent / 100) * (formData.absenteeismReductionPercent / 100) * 35;
    const fineSaving = formData.numVehicles * formData.finesPerVehicleYear * (formData.fineReductionPercent / 100) * formData.avgFineAmount;
    const maintSaving = formData.numVehicles * formData.maintenanceCostPerVehicle * (formData.maintenanceReductionPercent / 100);
    const totalOperativo = fuelSaving + accidentSaving + absentSaving + fineSaving + maintSaving;
    const co2 = (annualLiters * (formData.fuelReductionPercent / 100) * 2.68) / 1000;
    const litrosAhorrados = annualLiters * (formData.fuelReductionPercent / 100);
    const kmGratis = litrosAhorrados / (formData.fuelConsumption / 100);
    
    // CAES calculation
    const caesBruto = fuelSaving;
    const hwCost = formData.numVehicles * (config.precios?.hardwareUnitario || 180);
    const saasCost = formData.numVehicles * (config.precios?.saasAnual || 72);
    const caesNeto = caesBruto - hwCost - saasCost;
    
    // Total incluye ahorro operativo + CAES Neto
    const total = totalOperativo + caesNeto;
    
    setResultado({
      totalAnnualSavingEuros: Math.round(total),
      totalOperativo: Math.round(totalOperativo),
      fuelSavingEuros: Math.round(fuelSaving),
      accidentSavingEuros: Math.round(accidentSaving),
      absenteeismSavingEuros: Math.round(absentSaving),
      fineSavingEuros: Math.round(fineSaving),
      maintenanceSavingEuros: Math.round(maintSaving),
      co2ReductionTons: Math.round(co2),
      litrosAhorrados: Math.round(litrosAhorrados),
      kmGratis: Math.round(kmGratis),
      caesBruto: Math.round(caesBruto),
      caesNeto: Math.round(caesNeto),
      hwCost: Math.round(hwCost),
      saasCost: Math.round(saasCost)
    });
    setShowResults(true);
  };

  const steps = [
    { id: 'flota', title: 'Flota', icon: <Truck className="w-4 h-4" />,
      fields: [
        { key: 'numVehicles', label: 'Vehículos' },
        { key: 'kmPerVehicleYear', label: 'Km/año' },
        { key: 'fuelConsumption', label: 'L/100km' },
        { key: 'fuelPrice', label: '€/L' }
      ]
    },
    { id: 'kpis', title: 'KPIs', icon: <BarChart3 className="w-4 h-4" />,
      fields: [
        { key: 'accidentHoursPercent', label: 'Accid. %' },
        { key: 'absenteeismPercent', label: 'Absent. %' },
        { key: 'finesPerVehicleYear', label: 'Multas/veh' },
        { key: 'maintenanceCostPerVehicle', label: 'Mant. €' }
      ]
    },
    { id: 'comparativa', title: 'vs Sector', icon: <TrendingUp className="w-4 h-4" />, isAuto: true },
    { id: 'mejoras', title: 'Mejoras', icon: <Target className="w-4 h-4" />,
      fields: [
        { key: 'fuelReductionPercent', label: 'Comb. %' },
        { key: 'accidentReductionPercent', label: 'Accid. %' },
        { key: 'absenteeismReductionPercent', label: 'Bajas %' },
        { key: 'maintenanceReductionPercent', label: 'Mant. %' }
      ]
    }
  ];

  const getBenchStatus = (field, value) => {
    if (!currentBench) return null;
    const benchMap = { fuelConsumption: currentBench.consumo, accidentHoursPercent: currentBench.accidentes, absenteeismPercent: currentBench.absentismo };
    const bench = benchMap[field];
    if (!bench) return null;
    if (value <= bench.p25) return { label: 'Top25%', color: 'text-emerald-500' };
    if (value <= bench.media) return { label: 'Media', color: 'text-blue-500' };
    return { label: 'Mejorable', color: 'text-amber-500' };
  };

  // Generar PDF
  const handleGeneratePDF = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html><html><head><title>Quick Analysis - ${clientData.empresa}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', sans-serif; background: #f0fdf4; padding: 40px; }
        .header { background: linear-gradient(135deg, #f59e0b, #ea580c); color: white; padding: 30px; border-radius: 16px; margin-bottom: 30px; }
        .header h1 { font-size: 28px; margin-bottom: 8px; }
        .section { background: white; border-radius: 16px; padding: 24px; margin-bottom: 20px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); }
        .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
        .card { background: #f0fdf4; border-radius: 12px; padding: 16px; text-align: center; }
        .card h3 { color: #059669; font-size: 14px; margin-bottom: 8px; }
        .card .value { font-size: 28px; font-weight: 800; color: #047857; }
        .hero { background: linear-gradient(135deg, #059669, #047857); color: white; padding: 40px; border-radius: 16px; text-align: center; margin-bottom: 30px; }
        .hero .big { font-size: 56px; font-weight: 900; }
        .caes { background: linear-gradient(135deg, #0ea5e9, #0284c7); color: white; padding: 24px; border-radius: 16px; }
        .proceso { margin-top: 30px; }
        .fases { display: flex; justify-content: space-between; margin-top: 20px; }
        .fase { text-align: center; flex: 1; padding: 16px; background: #ecfdf5; border-radius: 12px; margin: 0 8px; }
        .fase h4 { color: #059669; font-size: 14px; margin-bottom: 8px; }
        .footer { text-align: center; margin-top: 30px; color: #6b7280; font-size: 12px; }
      </style></head><body>
      <div class="header">
        <h1>🚀 Quick Analysis: ${clientData.empresa}</h1>
        <p>${currentBench?.name || clientData.sector} • ${formData.numVehicles} vehículos</p>
      </div>
      <div class="hero">
        <p style="font-size: 18px; opacity: 0.9;">Ahorro Anual Estimado</p>
        <p class="big">${resultado?.totalAnnualSavingEuros?.toLocaleString('es-ES')} €</p>
        <p style="margin-top: 16px; font-size: 16px;">${resultado?.kmGratis?.toLocaleString('es-ES')} km sin pagar combustible</p>
      </div>
      <div class="section">
        <h2 style="margin-bottom: 16px; color: #047857;">📊 Desglose de Ahorros</h2>
        <div class="grid">
          <div class="card"><h3>Combustible</h3><p class="value">${resultado?.fuelSavingEuros?.toLocaleString('es-ES')} €</p></div>
          <div class="card"><h3>Accidentes</h3><p class="value">${resultado?.accidentSavingEuros?.toLocaleString('es-ES')} €</p></div>
          <div class="card"><h3>Bajas</h3><p class="value">${resultado?.absenteeismSavingEuros?.toLocaleString('es-ES')} €</p></div>
          <div class="card"><h3>Multas</h3><p class="value">${resultado?.fineSavingEuros?.toLocaleString('es-ES')} €</p></div>
          <div class="card"><h3>Mantenimiento</h3><p class="value">${resultado?.maintenanceSavingEuros?.toLocaleString('es-ES')} €</p></div>
          <div class="card"><h3>CO₂ Evitado</h3><p class="value">${resultado?.co2ReductionTons} ton</p></div>
        </div>
      </div>
      <div class="caes">
        <h2 style="margin-bottom: 16px;">⚡ CAES - Certificado de Ahorro Energético</h2>
        <div style="display: flex; justify-content: space-around; text-align: center;">
          <div><p style="opacity: 0.8;">CAES Bruto</p><p style="font-size: 32px; font-weight: 800;">${resultado?.caesBruto?.toLocaleString('es-ES')} €</p></div>
          <div><p style="opacity: 0.8;">Inversión</p><p style="font-size: 32px; font-weight: 800;">${(resultado?.hwCost + resultado?.saasCost)?.toLocaleString('es-ES')} €</p></div>
          <div><p style="opacity: 0.8;">CAES Neto</p><p style="font-size: 32px; font-weight: 800;">${resultado?.caesNeto?.toLocaleString('es-ES')} €</p></div>
        </div>
      </div>
      <div class="proceso">
        <h2 style="color: #047857; margin-bottom: 8px;">🎯 Cómo lo Conseguimos</h2>
        <p style="color: #6b7280; margin-bottom: 16px;">Proceso MOBILSAFE en 4 fases</p>
        <div class="fases">
          <div class="fase"><h4>PREVIO</h4><p style="font-size: 12px; color: #6b7280;">Análisis inicial y selección de vehículos</p></div>
          <div class="fase"><h4>F1</h4><p style="font-size: 12px; color: #6b7280;">Instalación y calibración</p></div>
          <div class="fase"><h4>F2</h4><p style="font-size: 12px; color: #6b7280;">Monitorización y optimización</p></div>
          <div class="fase"><h4>CAES</h4><p style="font-size: 12px; color: #6b7280;">Certificación del ahorro</p></div>
        </div>
      </div>
      <div class="footer">
        <p>Fuente: ${config.benchmarkSources || 'Datos MOBILSAFE'}</p>
        <p style="margin-top: 8px;">Generado por MOBILSAFE Impact Simulator</p>
      </div>
      </body></html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Vista de resultados (ocupa toda la pantalla)
  if (showResults && resultado) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-green-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-green-600 text-white py-4 px-6">
          <div className="max-w-6xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button onClick={() => setShowResults(false)} className="p-2 hover:bg-white/20 rounded-lg">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <img
                src="/logo-mobilsafe.png"
                alt="MOBILSAFE"
                className="h-9 md:h-10 bg-white/95 rounded px-2 py-1"
                onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
              />
              <div className="hidden items-center gap-2">
                <div className="w-9 h-9 bg-white/15 backdrop-blur rounded-lg flex items-center justify-center border border-white/30">
                  <Leaf className="w-5 h-5 text-white" />
                </div>
                <div className="leading-tight">
                  <p className="text-base font-black tracking-tight text-white">MOBILSAFE</p>
                  <p className="text-[9px] text-emerald-100 tracking-widest font-semibold">FLEET TECHNOLOGY</p>
                </div>
              </div>
              <div className="border-l border-white/30 pl-4">
                <h1 className="text-xl font-black">Resultado: {clientData.empresa}</h1>
                <p className="text-emerald-200 text-sm">{formData.numVehicles} vehículos • {currentBench?.name}</p>
              </div>
            </div>
            <button onClick={handleGeneratePDF} className="px-6 py-2.5 bg-white text-emerald-600 hover:bg-emerald-50 rounded-lg font-bold shadow-lg flex items-center gap-2" data-testid="quick-generate-pdf-btn">
              <Download className="w-5 h-5" /> Generar PDF
            </button>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 py-6">
          {/* Hero - Ahorro Total */}
          <div className="bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-2xl p-8 text-center mb-6 shadow-xl">
            <p className="text-emerald-100 text-lg mb-2">Ahorro Anual Estimado</p>
            <p className="text-6xl font-black mb-4">{resultado.totalAnnualSavingEuros.toLocaleString('es-ES')} €</p>
            <div className="flex justify-center gap-8 text-emerald-100">
              <div><p className="text-3xl font-bold">{resultado.kmGratis.toLocaleString('es-ES')}</p><p className="text-sm">km sin pagar</p></div>
              <div><p className="text-3xl font-bold">{resultado.litrosAhorrados.toLocaleString('es-ES')} L</p><p className="text-sm">combustible</p></div>
              <div><p className="text-3xl font-bold">{resultado.co2ReductionTons}</p><p className="text-sm">ton CO₂ evitado</p></div>
            </div>
          </div>

          {/* Grid de ahorros + CAES */}
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            {/* Desglose */}
            <div className="bg-white rounded-2xl p-5 shadow-lg border-2 border-emerald-200">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2"><Euro className="w-5 h-5 text-emerald-500" /> Desglose</h3>
              <div className="space-y-3">
                {[
                  { label: 'Combustible', value: resultado.fuelSavingEuros, color: 'bg-emerald-500' },
                  { label: 'Accidentes', value: resultado.accidentSavingEuros, color: 'bg-red-500' },
                  { label: 'Bajas', value: resultado.absenteeismSavingEuros, color: 'bg-amber-500' },
                  { label: 'Multas', value: resultado.fineSavingEuros, color: 'bg-orange-500' },
                  { label: 'Mantenimiento', value: resultado.maintenanceSavingEuros, color: 'bg-blue-500' }
                ].map(item => (
                  <div key={item.label} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${item.color}`} />
                      <span className="text-sm text-gray-600">{item.label}</span>
                    </div>
                    <span className="font-bold text-gray-800">{item.value.toLocaleString('es-ES')} €</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CAES */}
            <div className="bg-gradient-to-br from-cyan-500 to-blue-500 text-white rounded-2xl p-5 shadow-lg">
              <h3 className="font-bold mb-4 flex items-center gap-2"><Zap className="w-5 h-5" /> CAES</h3>
              <div className="space-y-4">
                <div className="bg-white/20 rounded-xl p-3 text-center">
                  <p className="text-xs opacity-80">Valor Anual CAES</p>
                  <p className="text-2xl font-black">{resultado.caesBruto.toLocaleString('es-ES')} €</p>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1 bg-white/10 rounded-lg p-2 text-center">
                    <p className="text-xs opacity-70">HW</p>
                    <p className="font-bold text-sm">{resultado.hwCost.toLocaleString('es-ES')} €</p>
                  </div>
                  <div className="flex-1 bg-white/10 rounded-lg p-2 text-center">
                    <p className="text-xs opacity-70">SaaS</p>
                    <p className="font-bold text-sm">{resultado.saasCost.toLocaleString('es-ES')} €</p>
                  </div>
                </div>
                <div className="bg-white/30 rounded-xl p-3 text-center">
                  <p className="text-xs opacity-80">CAES Neto</p>
                  <p className="text-2xl font-black">{resultado.caesNeto.toLocaleString('es-ES')} €</p>
                </div>
              </div>
            </div>

            {/* Impacto Ambiental */}
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-2xl p-5 shadow-lg">
              <h3 className="font-bold mb-4 flex items-center gap-2"><Leaf className="w-5 h-5" /> Impacto Ambiental</h3>
              <div className="space-y-4">
                <div className="bg-white/20 rounded-xl p-4 text-center">
                  <p className="text-xs opacity-80">CO₂ Evitado</p>
                  <p className="text-4xl font-black">{resultado.co2ReductionTons} ton</p>
                </div>
                <div className="bg-white/10 rounded-xl p-3 text-center">
                  <p className="text-xs opacity-80">Equivalente a</p>
                  <p className="text-xl font-bold">{Math.round(resultado.co2ReductionTons * 50)} árboles</p>
                  <p className="text-xs opacity-70">plantados al año</p>
                </div>
              </div>
            </div>
          </div>

          {/* Proceso CAES */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-emerald-200">
            <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2"><Target className="w-5 h-5 text-emerald-500" /> Cómo lo Conseguimos - Proceso MOBILSAFE</h3>
            <p className="text-gray-500 text-sm mb-4">Las fases F1 y F2 son críticas para garantizar el ahorro certificable</p>
            <div className="grid grid-cols-4 gap-3">
              {[
                { id: 'PREVIO', title: 'Análisis', desc: 'Selección estratégica de vehículos', color: 'from-gray-400 to-gray-500' },
                { id: 'F1', title: 'Instalación', desc: 'Calibración y puesta en marcha', color: 'from-emerald-400 to-emerald-500' },
                { id: 'F2', title: 'Optimización', desc: 'Monitorización y mejora continua', color: 'from-emerald-500 to-green-500' },
                { id: 'CAES', title: 'Certificación', desc: 'Ahorro certificable y cobrable', color: 'from-cyan-500 to-blue-500' }
              ].map((fase, idx) => (
                <div key={fase.id} className={`bg-gradient-to-br ${fase.color} text-white rounded-xl p-4 text-center relative`}>
                  {idx < 3 && <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 text-emerald-300 z-10"><ChevronRight className="w-6 h-6" /></div>}
                  <p className="text-xs opacity-80">{fase.id}</p>
                  <p className="font-bold">{fase.title}</p>
                  <p className="text-xs mt-1 opacity-80">{fase.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Fuentes */}
          <p className="text-center text-gray-400 text-xs mt-4">
            Fuente: {config.benchmarkSources}
          </p>
        </div>
      </div>
    );
  }

  // Vista de formulario (columnas)
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 px-4 shrink-0">
        <div className="max-w-5xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-2 hover:bg-white/20 rounded-lg" data-testid="back-from-quick-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <img
              src="/logo-mobilsafe.png"
              alt="MOBILSAFE"
              className="h-9 md:h-10 bg-white/95 rounded px-2 py-1"
              onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
            />
            <div className="hidden items-center gap-2">
              <div className="w-9 h-9 bg-white/15 backdrop-blur rounded-lg flex items-center justify-center border border-white/30">
                <Leaf className="w-5 h-5 text-white" />
              </div>
              <div className="leading-tight">
                <p className="text-base font-black tracking-tight text-white">MOBILSAFE</p>
                <p className="text-[9px] text-amber-100 tracking-widest font-semibold">FLEET TECHNOLOGY</p>
              </div>
            </div>
            <div className="border-l border-white/30 pl-3">
              <h1 className="text-lg font-black flex items-center gap-2"><Rocket className="w-5 h-5" /> Quick: {clientData.empresa}</h1>
              <p className="text-amber-100 text-xs">
                {sectorNames[clientData.sector] || clientData.sector} • {comercialActual?.nombre || 'Sin comercial'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Progress bar - sticky */}
      <div className="bg-white/80 backdrop-blur-sm py-2 px-4 shadow-sm shrink-0 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between bg-amber-50 rounded-full p-1">
            {steps.map((step, idx) => (
              <button
                key={step.id}
                onClick={() => idx <= activeStep && setActiveStep(idx)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-full text-xs font-bold transition-all ${
                  idx === activeStep ? 'bg-amber-500 text-white shadow-md' : idx < activeStep ? 'bg-emerald-500 text-white' : 'text-gray-400'
                }`}
              >
                {idx < activeStep ? <Check className="w-3 h-3" /> : step.icon}
                <span>{step.title}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Contenido principal - flex-grow */}
      <div className="flex-grow overflow-auto pb-20">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="grid grid-cols-4 gap-3">
            {steps.map((step, idx) => (
              <div
                key={step.id}
                className={`rounded-xl border-2 transition-all flex flex-col ${
                  idx === activeStep ? 'border-amber-400 bg-white shadow-lg ring-2 ring-amber-200' : idx < activeStep ? 'border-emerald-300 bg-emerald-50/50' : 'border-gray-200 bg-gray-50/50 opacity-50'
                }`}
              >
                {/* Header de columna */}
                <div className={`p-2.5 rounded-t-lg flex items-center gap-2 ${
                  idx === activeStep ? 'bg-amber-500 text-white' : idx < activeStep ? 'bg-emerald-500 text-white' : 'bg-gray-300 text-gray-500'
                }`}>
                  {idx < activeStep ? <Check className="w-4 h-4" /> : step.icon}
                  <span className="font-bold text-sm">{step.title}</span>
                </div>

                {/* Contenido */}
                <div className="flex-1 p-3">
                  {/* Mensaje teaser para pasos futuros */}
                  {idx > activeStep && step.id !== 'flota' && (
                    <div className="h-full flex flex-col items-center justify-center text-center p-2">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center mb-3">
                        {step.id === 'kpis' && <TrendingUp className="w-6 h-6 text-amber-500" />}
                        {step.id === 'comparativa' && <BarChart3 className="w-6 h-6 text-amber-500" />}
                        {step.id === 'mejoras' && <Target className="w-6 h-6 text-amber-500" />}
                      </div>
                      <p className="text-sm font-bold text-gray-700 mb-1">
                        {step.id === 'kpis' && mensajesSeleccionados.kpis?.headline}
                        {step.id === 'comparativa' && mensajesSeleccionados.vsSector?.headline}
                        {step.id === 'mejoras' && mensajesSeleccionados.mejoras?.headline}
                      </p>
                      <p className="text-xs text-gray-500">
                        {step.id === 'kpis' && mensajesSeleccionados.kpis?.subtext}
                        {step.id === 'comparativa' && mensajesSeleccionados.vsSector?.subtext}
                        {step.id === 'mejoras' && mensajesSeleccionados.mejoras?.subtext}
                      </p>
                    </div>
                  )}

                  {/* Campos activos o completados */}
                  {idx <= activeStep && step.fields && (
                    <div className="space-y-2">
                      {step.fields.map(field => {
                        const status = getBenchStatus(field.key, formData[field.key]);
                        return (
                          <div key={field.key}>
                            <label className="text-xs text-gray-500 block mb-0.5">{field.label}</label>
                            <input
                              type="number"
                              value={formData[field.key]}
                              onChange={(e) => setFormData(prev => ({ ...prev, [field.key]: parseFloat(e.target.value) || 0 }))}
                              disabled={idx !== activeStep}
                              className={`w-full px-2 py-1.5 border-2 rounded-lg text-center font-bold text-sm transition-all ${
                                idx === activeStep ? 'border-amber-300 bg-white focus:border-amber-500 focus:ring-1 focus:ring-amber-200' : 'border-gray-200 bg-gray-100'
                              }`}
                              data-testid={`quick-input-${field.key}`}
                            />
                            {status && idx === activeStep && <p className={`text-xs mt-0.5 ${status.color}`}>{status.label}</p>}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {idx <= activeStep && step.id === 'comparativa' && (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-400 text-center mb-2">vs {sectorNames[clientData.sector] || clientData.sector}</p>
                      {[
                        { label: 'Consumo', yours: formData.fuelConsumption, sector: currentBench?.consumo?.media, unit: 'L/100km' },
                        { label: 'Accidentes', yours: formData.accidentHoursPercent, sector: currentBench?.accidentes?.media, unit: '%' },
                        { label: 'Absentismo', yours: formData.absenteeismPercent, sector: currentBench?.absentismo?.media, unit: '%' }
                      ].map(item => {
                        const isBetter = item.yours <= (item.sector || 999);
                        return (
                          <div key={item.label} className={`p-2 rounded-lg ${isBetter ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                            <p className="text-xs text-gray-500 mb-1">{item.label}</p>
                            <div className="flex justify-between items-center">
                              <span className={`font-bold text-sm ${isBetter ? 'text-emerald-600' : 'text-amber-600'}`}>
                                Tú: {item.yours}
                              </span>
                              <span className="text-xs text-gray-400">
                                Sector: {item.sector || '-'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Fuentes */}
        <div className="max-w-5xl mx-auto px-4">
          <p className="text-xs text-gray-400 text-center">
            <FileText className="w-3 h-3 inline mr-1" />
            {config.benchmarkSources}
          </p>
        </div>
      </div>

      {/* Botón fijo en la parte inferior */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-amber-200 py-3 px-4 shadow-lg z-20">
        <div className="max-w-5xl mx-auto flex justify-between items-center">
          <p className="text-sm text-gray-500">
            <span className="font-bold text-amber-600">{activeStep + 1}</span> de {steps.length} pasos completados
          </p>
          {activeStep < steps.length - 1 ? (
            <button
              onClick={() => setActiveStep(prev => prev + 1)}
              className="px-6 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-bold text-sm flex items-center gap-2 shadow-md transition-all hover:scale-105"
              data-testid="quick-next-btn"
            >
              Siguiente <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={calcularResultado}
              className="px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-bold text-sm flex items-center gap-2 shadow-md transition-all hover:scale-105"
              data-testid="quick-ver-resultado-btn"
            >
              Ver Resultado <Award className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickAnalysis;
